import { PropsWithChildren } from 'react';
import { CalendarWeek, LockFill, PlayFill } from 'react-bootstrap-icons';
import { useNavigate } from 'react-router-dom';

import { Button, Typography } from 'antd';

import { aspectRatioPadding } from '@/components/shared/ImageRatio';
import Progress from '@/components/shared/Progress';
import { formatDate } from '@/helpers/formatDate';
import { enStatus, IMyCourse } from '@/interfaces/course';
import styled, { css, StyledProp } from '@/styled';

interface IProps extends StyledProp {
  course: Partial<IMyCourse>;
}

const MoreInfo = ({ course, className, children }: PropsWithChildren<IProps>) => {
  const navigate = useNavigate();

  const goToCourse = () => {
    if (course?.last_lesson_access) {
      navigate(`/curso/${course.hash}/aula/${course?.last_lesson_access}`);
    } else {
      navigate(`/curso/${course.hash}`);
    }
  };

  const nameMapper: Record<string, any> = {
    [enStatus.EXPIRED]: 'Curso expirado',
    [enStatus.BLOCKED]: 'Curso bloqueado',
    [enStatus.NOT_RELEASED]: 'Agendado para o dia ' + formatDate(course.release_at)
  };

  const iconMapper: Record<string, any> = {
    [enStatus.EXPIRED]: <LockFill size={24} />,
    [enStatus.BLOCKED]: <LockFill size={24} />,
    [enStatus.NOT_RELEASED]: <CalendarWeek size={24} />
  };

  const showTagStatus = [enStatus.EXPIRED, enStatus.BLOCKED, enStatus.NOT_RELEASED].includes(course.status);
  const progress = course.progress_percent ?? 0;

  const continueText = progress > 0 ? 'Continuar' : 'Acessar';
  const buttonText = showTagStatus ? 'Ver curso' : continueText;

  return (
    <div className={className}>
      <Button type='primary' onClick={goToCourse} id={`btn-${course.hash}`}>
        <Typography.Title level={5}>{buttonText}</Typography.Title> <PlayFill size={24} />
      </Button>
      <Progress percent={progress} showInfo={false} status='normal' />
      <Typography.Text id={`progress-bar-text-${course.hash}`}>{progress}% concluído</Typography.Text>
      <div className='children'>{children}</div>
      {showTagStatus && (
        <div className='statusContainer'>
          {iconMapper[course.status]}
          <Typography.Paragraph className='statusLabel'>{nameMapper[course.status]}</Typography.Paragraph>
        </div>
      )}
    </div>
  );
};

export default styled(MoreInfo)(({ theme }) => {
  return css`
    padding: ${theme.space.spacing(4)};
    background: ${theme.variables.bgElevated};
    border-bottom-right-radius: ${theme.borderRadius}px;
    border-bottom-left-radius: ${theme.borderRadius}px;
    position: absolute;
    top: 0;
    width: 100%;
    opacity: 0;
    pointer-events: none;
    transition: ${theme.motionDurationMid};
    margin-top: calc(${aspectRatioPadding['16:9']} - 8px);
    border: 1px solid transparent;
    border-top: 0;
    box-sizing: border-box;

    .statusContainer {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      width: 100%;
      margin-top: ${theme.space.spacing(4)};
      gap: ${theme.space.spacing(2)};

      .statusLabel {
        white-space: nowrap;
        height: 24px;
        line-height: 1.8;
        margin: 0;
      }

      .statusLabel,
      svg {
        color: ${theme.colorTextTertiary};
      }
    }

    .children {
      margin-top: ${theme.space.spacing(2)};
    }

    .ant-progress {
      margin: 0;
      width: 100%;
      margin-bottom: ${theme.space.spacing(1)};
    }

    button {
      h5 {
        margin: 0;
        color: ${theme.variables.myCoursesCardButtonText};
      }

      svg {
        color: ${theme.variables.myCoursesCardButtonText};
      }

      margin-bottom: ${theme.space.spacing(4)};
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: ${theme.space.spacing(2)};
    }

    > h4 {
      color: ${theme.colorText};
      margin-top: ${theme.space.spacing(4)};
    }

    > span {
      color: ${theme.colorText};
    }

    > span:last-child {
      color: ${theme.colorTextTertiary};
    }

    &:after {
      content: '';
      width: 100%;
      height: 30px;
      position: absolute;
      top: -30px;
      left: 0;
      background: linear-gradient(180deg, transparent 20%, ${theme.colorBgBase} 100%);
    }
  `;
});
